// VideoCatalog: renders a list of groups (e.g. playlists), each paginated.
// Pure presentation — takes typed groups + page state via props. UI knows
// nothing about how the data was fetched or which JSON it came from.

import { VideoTile, type VideoTileProps } from '@ui/library';
import { paginate } from '@lib/adapters/youtube-catalog';
import type { VideoGroup } from '@lib/adapters/youtube-catalog';

export interface VideoCatalogCopy {
  videosLabel: string;             // "{n} videos"
  pageLabel: string;               // "Page {current} of {total}"
  prevLabel: string;               // "Prev"
  nextLabel: string;
  playLabel: string;               // "Play video"
  emptyLabel?: string;             // "No videos match '{query}'."
}

export interface VideoCatalogProps {
  groups: ReadonlyArray<VideoGroup>;
  pageSize: number;
  /** Per-group page state: groupTitle → currentPage (1-indexed). */
  pages: Readonly<Record<string, number>>;
  /** Called when the user changes the page in a group. */
  onPageChange: (groupTitle: string, page: number) => void;
  /** Free-text filter. Matches video title, channel, group name, tags. */
  filter?: string;
  copy: VideoCatalogCopy;
}

function matchesFilter(video: VideoGroup['videos'][number], groupTitle: string, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (video.title.toLowerCase().includes(needle)) return true;
  if (video.channelTitle.toLowerCase().includes(needle)) return true;
  if (groupTitle.toLowerCase().includes(needle)) return true;
  if (video.tags.some((t) => t.toLowerCase().includes(needle))) return true;
  if (String(video.year).includes(needle)) return true;
  return false;
}

function formatTemplate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

export function VideoCatalog({
  groups,
  pageSize,
  pages,
  onPageChange,
  filter,
  copy,
}: VideoCatalogProps) {
  const q = (filter ?? '').trim();

  // Filter videos per group; drop groups that end up empty.
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      videos: g.videos.filter((v) => matchesFilter(v, g.title, q)),
    }))
    .filter((g) => g.videos.length > 0);

  if (q && visibleGroups.length === 0) {
    return (
      <div className="video-catalog video-catalog--empty">
        <p className="video-catalog__empty">
          {formatTemplate(copy.emptyLabel ?? "No videos match '{q}'.", { q })}
        </p>
      </div>
    );
  }

  return (
    <div className="video-catalog">
      {visibleGroups.map((group) => {
        // When filtering, force page 1 — collapsing matches shouldn't leave the
        // user stranded on an empty out-of-range page.
        const currentPage = q ? 1 : (pages[group.title] ?? 1);
        const result = paginate(group.videos, currentPage, pageSize);
        return (
          <section key={group.title} className="video-catalog__group">
            <header className="video-catalog__group-head">
              <h2 className="video-catalog__group-title">{group.title}</h2>
              <span className="video-catalog__group-meta">
                {formatTemplate(copy.videosLabel, { n: group.videos.length })}
              </span>
            </header>

            <div className="video-catalog__grid">
              {result.items.map((video, i) => {
                const overrideThumbnail = group.customThumbnails[video.videoId];
                const tileProps: VideoTileProps = {
                  video,
                  staggerIndex: i,
                  playLabel: copy.playLabel,
                  ...(overrideThumbnail ? { customThumbnail: overrideThumbnail } : {}),
                };
                return <VideoTile key={video.videoId} {...tileProps} />;
              })}
            </div>

            {result.totalPages > 1 && (
              <nav className="video-catalog__pagination" aria-label={`Pagination for ${group.title}`}>
                <button
                  type="button"
                  className="video-catalog__page-btn"
                  onClick={() => onPageChange(group.title, result.page - 1)}
                  disabled={result.page <= 1}
                >
                  ← {copy.prevLabel}
                </button>
                <span className="video-catalog__page-info">
                  {formatTemplate(copy.pageLabel, {
                    current: result.page,
                    total: result.totalPages,
                  })}
                </span>
                <button
                  type="button"
                  className="video-catalog__page-btn"
                  onClick={() => onPageChange(group.title, result.page + 1)}
                  disabled={result.page >= result.totalPages}
                >
                  {copy.nextLabel} →
                </button>
              </nav>
            )}
          </section>
        );
      })}
    </div>
  );
}
